import { PricingRaiseBadge } from '../inventory/PricingRaisedNotice';
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { SignOutButton } from './SignOutButton';
import { WriteRefusalNotice } from '../ui/WriteRefusalNotice';
import { EmailBanner } from './EmailBanner';
import { BrandMark } from './BrandMark';
import { Bell } from './Bell';
import { MobileTabBar } from './MobileTabBar';
import { zonesForRole } from '../nav';
import { useSession } from '../session';
import { useTheme } from '../theme';
import { RunningTimerChip } from '../costing/RunningTimerChip';
import { AlertCountBadge, AlertCountWords, useAlertCount } from '../alerts/AlertCount';
import { IconSettings, IconTheme } from '../icons';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The office shell — PRD v2 §4 chrome on the vendored shadcn sidebar (amendment A-4/D10):
 * sidebar-only chrome (no top bar), two zones from `nav.ts` (still the single source of
 * truth), Settings + account in the footer. Desktop: fixed sidebar, collapsible to an
 * icon rail. Mobile (< lg): the sidebar renders as a modal drawer (Radix Sheet — focus
 * trap comes from the primitive) behind an IN-FLOW menu bar. In-flow, not floating, so
 * it can never occlude a page's h1 or back-crumb — and `settings-anchors` measures its
 * height, so it stays the one sticky bar in this file.
 *
 * The DOCUMENT is the scrollport, as it was before the rebuild — the 012 scroll rail and
 * the anchor-offset contract both pin against the viewport. The vendored wrapper is
 * patched not to clip (see `Servwell patch (101)` in sidebar.tsx).
 */
export function OfficeShell({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const org = useQuery(trpc.org.info.queryOptions());
  // The org's name is never absent — it has one before the shell can render. While it
  // loads, show a skeleton, never the data-honesty dash ('—' means "we don't have this").
  const orgName = org.data?.name;
  const orgUnknown = org.isError || org.fetchStatus === 'paused';
  // The drawer's opener. Radix's default close-restore was measured landing on <body>
  // (React 19.1), so the shell owns the restore explicitly via onMobileCloseAutoFocus —
  // the same contract the hand-rolled drawer honored before the shadcn rebuild.
  const openerRef = useRef<HTMLButtonElement>(null);
  return (
    <SidebarProvider>
      <OfficeSidebar openerRef={openerRef} orgName={orgName} orgUnknown={orgUnknown} />
      {/* `sw-has-tabbar` is what switches `--sw-bottom-chrome` / `--sw-page-bottom` on,
          and it is set HERE rather than on `:root` because it is a fact about this shell:
          the tech shell has no bottom chrome, and neither does the desktop office shell.
          A global would make every chrome-less surface opt out of a bar it never had. */}
      {/* `min-w-0`: the inset is a flex item beside the sidebar, and a flex item will not
          shrink below its content's widest line unless told to, so a wide table or board
          widened <main> and the whole page scrolled sideways instead of the table (Jobs at
          1488px, Pipeline at 1536px, in a 1280px window: Phase 2 QA, R2-I1). Here, the one
          shell every office page renders in, rather than inside the vendored SidebarInset. */}
      <SidebarInset
        className="sw-has-tabbar min-w-0"
        style={{ paddingBottom: 'var(--sw-page-bottom)' }}
      >
        <MobileBar orgName={orgName} orgUnknown={orgUnknown} />
        {/* A change this device would not send (after an offline start: "needs a
            connection"), above every office page. */}
        <WriteRefusalNotice />
        {/* MAP 249 ticket 07: the most urgent thing about the shop's email (paused, held, the
            owner unconfirmed, a broken route, emails waiting), for the owner and the office,
            linking to the exact control in Settings → Email. In-flow, like the notice above. */}
        <EmailBanner />
        {children}
        <MobileTabBar openerRef={openerRef} />
      </SidebarInset>
    </SidebarProvider>
  );
}

function OrgName({ name, unknown }: { name: string | undefined; unknown: boolean }) {
  // Four-branch honesty at shell scale: actively loading → skeleton; a FAILED query or
  // one PAUSED offline with nothing cached → the data-honesty dash (we genuinely don't
  // know the name), never a skeleton that promises an answer that isn't coming.
  if (name !== undefined) return <>{name}</>;
  if (unknown) return <>—</>;
  return <Skeleton className="inline-block h-4 w-28 align-middle" />;
}

/**
 * The one `sticky top-0 … lg:hidden` bar — the anchor-offset contract's referent.
 *
 * ⚠️ It NO LONGER OPENS ANYTHING (116). The hamburger moved to the bottom bar's More
 * button, which is now the drawer's only opener; what is left is the shop's identity, which
 * is the half of this bar a phone user actually reads. The bar itself has to stay: it is
 * what `--sw-anchor-offset` clears, and `settings-anchors` requires this file to hold
 * exactly one sticky bar. The brand mark takes the button's place so the bar keeps a
 * height the offset already covers — measured in the browser, not assumed.
 */
function MobileBar({
  orgName,
  orgUnknown,
}: {
  orgName: string | undefined;
  orgUnknown: boolean;
}) {
  // The viewport the bar is drawn at (the app's MOBILE_QUERY, `lg:hidden`'s twin): only the
  // bell that is SHOWN polls.
  const { isMobile } = useSidebar();
  return (
    <div className="sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-surface px-3 py-2 lg:hidden">
      <BrandMark className="h-7 w-auto shrink-0" />
      {/* A div, not <p>/<span>: OrgName's loading branch is a Skeleton <div>, and flow
          content inside phrasing content is invalid HTML — React flags the <p> case as a
          would-be hydration error on every cold load. Same box either way (flex item). */}
      <div className="min-w-0 truncate text-sm font-semibold">
        <OrgName name={orgName} unknown={orgUnknown} />
      </div>
      {/* The phone's bell (ticket 05) sits IN this bar: no second sticky bar. A 44 px touch
          target pulled in by `-my-1`, so it lays out at 36 px and the bar keeps the 53 px
          `--sw-anchor-offset` clears; its popover stops above the tab bar. */}
      <Bell className="-my-1 ml-auto size-11" clearsTabBar polls={isMobile} />
    </div>
  );
}

function OfficeSidebar({
  openerRef,
  orgName,
  orgUnknown,
}: {
  openerRef: RefObject<HTMLButtonElement | null>;
  orgName: string | undefined;
  orgUnknown: boolean;
}) {
  const session = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [themePref, cycleTheme] = useTheme();
  const { isMobile, setOpenMobile } = useSidebar();
  /** What the bell's popover opens beside: the whole header, so it clears the sidebar. */
  const header = useRef<HTMLDivElement>(null);

  const zones = zonesForRole(session.role);
  const settingsActive = pathname.startsWith('/settings');
  // The Alerts count (Phase 7): the sidebar's copy polls at a desktop; on a phone the More
  // button's does (this sidebar is then a closed drawer). Both read the one count.
  const alertCount = useAlertCount(!isMobile);

  // A drawer opened at mobile width must not survive a trip through desktop and pop
  // back open, focus-trapped, when the viewport shrinks again.
  useEffect(() => {
    if (!isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);
  const closeDrawer = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar
      collapsible="icon"
      onMobileCloseAutoFocus={(e) => {
        e.preventDefault();
        openerRef.current?.focus();
      }}
    >
      <SidebarHeader ref={header}>
        {/* Collapsed to icons the row stacks (mark, bell, collapse), so the bell stays in
            the rail, visible and usable (ticket 05). */}
        <div className="flex items-center gap-2 px-1 py-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:px-0">
          <BrandMark className="h-7 w-auto shrink-0" />
          <div className="min-w-0 group-data-[collapsible=icon]:hidden">
            {/* A div, not <p> — OrgName's loading branch is a Skeleton <div>, and
                <p><div> is invalid HTML React flags as a would-be hydration error on
                every cold load. Same box (p and div are both blocks). */}
            <div className="truncate text-sm font-semibold leading-tight">
              <OrgName name={orgName} unknown={orgUnknown} />
            </div>
            <p className="text-xs text-muted-foreground">Servwell</p>
          </div>
          {/* The bell, beside the collapse button (ticket 05; the phone's is in MobileBar,
              so this one is hidden in the drawer). Its popover opens to the RIGHT of the
              header, over the page, never over the sidebar it came from. */}
          <Bell
            side="right"
            align="start"
            besideRef={header}
            polls={!isMobile}
            className="ml-auto size-7 group-data-[collapsible=icon]:ml-0 max-lg:hidden"
          />
          {/* The keyboard path to collapse — the drag rail is pointer-only and the
              Cmd/Ctrl+B shortcut is undiscoverable. Hidden on mobile: the drawer has
              its own close button. */}
          <SidebarTrigger className="shrink-0 max-lg:hidden" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label="Main">
          {zones.map((zone) => (
            <SidebarGroup key={zone.label}>
              <SidebarGroupLabel className="font-heading text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {zone.label}
              </SidebarGroupLabel>
              <SidebarMenu>
                {zone.items.map((item) => {
                  const active =
                    pathname === item.to || pathname.startsWith(item.to + '/');
                  return (
                    <SidebarMenuItem key={item.to}>
                      {/* `nav-active` (unlayered CSS) deliberately outranks the vendored
                          `data-[active=true]` utilities — cascade layers, not specificity,
                          decide, and unlayered author CSS beats every Tailwind layer. The
                          brand pill is the app's active-state identity. */}
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.label}
                        className={active ? 'nav-active' : ''}
                      >
                        <Link
                          to={item.to}
                          onClick={closeDrawer}
                          aria-current={active ? 'page' : undefined}
                        >
                          <item.icon
                            size={17}
                            className={active ? '' : 'text-muted-foreground'}
                          />
                          <span>
                            {item.label}
                            {item.pill === 'alerts' ? (
                              <AlertCountWords count={alertCount} />
                            ) : null}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                      {/* The count, BESIDE the button and not in it: the button clips its
                          content (`overflow-hidden`, 32 px square in the icon rail), so a
                          badge inside would vanish when the sidebar collapses. The item is
                          `relative`; collapsed, the badge moves onto the icon's corner. */}
                      {item.pill === 'priceRaises' ? <PricingRaiseBadge /> : null}
                      {item.pill === 'alerts' ? (
                        <AlertCountBadge
                          count={alertCount}
                          className="absolute right-1 top-2 group-data-[collapsible=icon]:-right-1 group-data-[collapsible=icon]:-top-1"
                        />
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </nav>
      </SidebarContent>
      <SidebarFooter>
        <RunningTimerChip onNavigate={closeDrawer} />
        <SidebarMenu>
          {session.role === 'owner' || session.role === 'office' ? (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={settingsActive}
                tooltip="Settings"
                className={settingsActive ? 'nav-active' : ''}
              >
                <Link
                  to="/settings"
                  onClick={closeDrawer}
                  aria-current={settingsActive ? 'page' : undefined}
                >
                  <IconSettings size={17} className="text-muted-foreground" />
                  <span>Settings</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : null}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={cycleTheme} tooltip="Theme">
              <IconTheme size={17} className="text-muted-foreground" />
              <span>Theme: {themePref}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 group-data-[collapsible=icon]:hidden">
          <div className="min-w-0">
            <p className="truncate text-xs font-medium">
              {session.name ?? session.email}
            </p>
            <p className="truncate text-[11px] text-muted-foreground">{session.role}</p>
          </div>
          <SignOutButton iconSize={16} />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
